import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getNotesAction } from "../../../../../store/actions/NotesAction";
import { Button } from "react-bootstrap";
import { formatDate } from "../../../../../utils/dateUtils";
import AddNotes from "./AddNotes.Component";
import PropTypes from "prop-types";

export const NotesHistory = ({
  casePaperData,
  patientId: propPatientId,
}) => {

  const dispatch = useDispatch();

  const [showAddNoteModal, setShowAddNoteModal] =
    useState(false);

  const [defaultCategory, setDefaultCategory] =
    useState(null);

  // Show More / Show Less
  const [expandedCategories, setExpandedCategories] =
    useState({});

  const isCasePaperMode =
    Boolean(casePaperData);

  const reduxPatientId = useSelector(
    (state) =>
      state.prescriptionState?.patientDetails
        ?.patient_id ||
      state.patients?.patientDetails
        ?.patient_id
  );

  const patientId =
    propPatientId ?? reduxPatientId;

  const notesFromStore = useSelector(
    (state) => state.notes?.notes || []
  );

  const notes = isCasePaperMode
    ? casePaperData?.Notes || []
    : notesFromStore;

  /**
   * Fetch Notes
   */
  useEffect(() => {

    if (
      !isCasePaperMode &&
      patientId
    ) {

      dispatch(
        getNotesAction(patientId)
      );

    }

  }, [
    dispatch,
    patientId,
    isCasePaperMode,
  ]);

  /**
   * Categories
   */
  const sortedCategories = useMemo(() => {

    const categoryMap = new Map();

    if (
      !notes ||
      notes.length === 0
    ) {
      return [];
    }

    notes.forEach((note) => {

      let categoryId =
        note.note_category;

      let categoryName =
        null;

      let bgColor =
        "#f8f9fa";

      let textColor =
        "#000000";

      const cat =
        note.category_details;

      if (
        cat?.category_text
      ) {

        categoryName =
          cat.category_text;

        categoryId =
          Number(
            cat.category_id
          );

        bgColor =
          cat.bg_color ||
          "#f8f9fa";

        textColor =
          cat.forground_color ||
          "#000000";

      } else {

        const categoryMapNames = {
          1: "Doctor Notes",
          2: "Service Notes",
          3: "General Notes",
          4: "Telecaller Notes",
          5: "Important Notes",
        };

        categoryId =
          Number(
            categoryId || 0
          );

        categoryName =
          categoryMapNames[
            categoryId
          ] ||
          "General Notes";
      }

      if (
        !categoryMap.has(
          `${categoryId}-${categoryName}`
        )
      ) {

        categoryMap.set(
          `${categoryId}-${categoryName}`,
          {
            id: categoryId,
            name: categoryName,
            bg: bgColor,
            textColor,
            btn: "primary",
          }
        );

      }

    });

    return Array.from(
      categoryMap.values()
    ).sort(
      (a, b) => a.id - b.id
    );

  }, [notes]);

  /**
   * Toggle Expand
   */
  const toggleExpand = (
    category
  ) => {

    setExpandedCategories(
      (prev) => ({
        ...prev,
        [category]:
          !prev[category],
      })
    );

  };

  /**
   * Empty state
   */
  if (
    !notes ||
    notes.length === 0
  ) {

    return (
      <div className="card shadow-none">
        <div
          className="card-body d-flex justify-content-center align-items-center"
          style={{
            minHeight: "120px",
          }}
        >
          <p className="text-muted mb-0 text-center">
            {isCasePaperMode
              ? "No notes available in case paper."
              : "No notes found."}
          </p>
        </div>
      </div>
    );

  }

  return (
    <div className="card shadow-none">

      <div
        className="card-body"
        style={{
          marginLeft: "-30px",
        }}
      >

        <div className="row">

          {sortedCategories.map(
            (config) => {

              const category =
                config.name;

              /**
               * Category Notes
               */
              const categoryNotes =
                notes.filter(
                  (note) => {

                    const categoryId =
                      Number(
                        note.note_category ||
                        0
                      );

                    const categoryText =
                      note
                        .category_details
                        ?.category_text;

                    // Priority 1
                    if (
                      categoryText
                    ) {

                      return (
                        categoryText ===
                        category
                      );

                    }

                    // Priority 2
                    if (
                      categoryId >
                        0 &&
                      categoryId ===
                        config.id
                    ) {

                      return true;

                    }

                    // Priority 3
                    if (
                      config.id ===
                        0 &&
                      (
                        note.note_category ===
                          null ||
                        note.note_category ===
                          undefined ||
                        note.note_category ===
                          "0" ||
                        note.note_category ===
                          0
                      )
                    ) {

                      return true;

                    }

                    return false;

                  }
                );

              if (
                categoryNotes.length ===
                0
              ) {
                return null;
              }

              return (

                <div
                  key={`category-${config.id}-${category}`}
                  className="col-md-4 mb-4"
                >

                  <div
                    className="border rounded-3 p-3 shadow-sm"
                    style={{
                      backgroundColor:
                        config.bg,

                      // ✅ Reduced height from 420px to 320px
                      height: "320px",

                      overflow: "hidden",

                      display: "flex",

                      flexDirection:
                        "column",

                      borderRadius:
                        "12px",
                    }}
                  >

                    {/* HEADER */}

                    <div className="d-flex justify-content-between align-items-center mb-2">

                      <h6
                        className="fw-bold mb-0"
                        style={{
                          color:
                            config.textColor,

                          fontSize:
                            "15px",
                        }}
                      >
                        {category}
                      </h6>

                      {!isCasePaperMode && (

                        <Button
                          variant={
                            config.btn
                          }

                          size="sm"

                          className="rounded-circle d-flex align-items-center justify-content-center"

                          style={{
                            width:
                              "30px",

                            height:
                              "30px",

                            padding:
                              "0",

                            fontSize:
                              "18px",

                            lineHeight:
                              "1",

                            flexShrink:
                              0,
                          }}

                          onClick={() => {

                            setDefaultCategory(
                              {
                                id: config.id,
                                name: category,
                              }
                            );

                            setShowAddNoteModal(
                              true
                            );

                          }}
                        >
                          +
                        </Button>

                      )}

                    </div>

                    {/* NOTES AREA */}

                    <div
                      className="mt-2"
                      style={{

                        flexGrow: 1,

                        // EXACT HEIGHT FOR 3 NOTES
                        height:
                          expandedCategories[
                            category
                          ]
                            ? "220px"
                            : "150px",

                        // SCROLL ONLY AFTER SHOW MORE
                        overflowY:
                          expandedCategories[
                            category
                          ]
                            ? "auto"
                            : "hidden",

                        overflowX:
                          "hidden",

                        paddingRight:
                          "6px",

                        transition:
                          "all 0.3s ease",
                      }}
                    >

                      {(
                        expandedCategories[
                          category
                        ]
                          ? categoryNotes
                          : categoryNotes.slice(
                              0,
                              3
                            )
                      ).map((note) => (

                        <div
                          key={`note-${note.note_id}`}

                          className="p-3 mb-2 border rounded-3"

                          style={{
                            backgroundColor:
                              "rgba(255,255,255,0.92)",

                            boxShadow:
                              "0 1px 4px rgba(0,0,0,0.08)",
                          }}
                        >

                          <small
                            className="text-muted d-block mb-1"
                            style={{
                              fontSize:
                                "12px",
                            }}
                          >
                            {formatDate(
                              note.created_at
                            )}
                          </small>

                          <p
                            className="mb-0 text-muted"
                            style={{
                              fontSize:
                                "14px",

                              lineHeight:
                                "1.5",

                              wordBreak:
                                "break-word",
                            }}
                          >
                            {
                              note.note_description
                            }
                          </p>

                        </div>

                      ))}

                    </div>

                    {/* SHOW MORE / SHOW LESS */}

                    {categoryNotes.length >
                      3 && (

                      <button
                        className="btn btn-link p-0 mt-2 text-decoration-none"

                        style={{
                          fontSize:
                            "13px",

                          alignSelf:
                            "center",

                          fontWeight:
                            "500",

                          width:
                            "100%",
                        }}

                        onClick={() =>
                          toggleExpand(
                            category
                          )
                        }
                      >

                        {expandedCategories[
                          category
                        ]
                          ? "▲ Show Less"
                          : "▼ Show More"}

                      </button>

                    )}

                  </div>

                </div>

              );

            }
          )}

        </div>

      </div>

      {/* ADD NOTES MODAL */}

      {!isCasePaperMode &&
        showAddNoteModal && (

          <AddNotes
            showModal={
              showAddNoteModal
            }

            handleClose={() =>
              setShowAddNoteModal(
                false
              )
            }

            defaultCategory={
              defaultCategory
            }

            patientId={patientId}
          />

        )}

    </div>
  );
};

NotesHistory.propTypes = {
  casePaperData:
    PropTypes.object,

  patientId:
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
};

NotesHistory.defaultProps = {
  casePaperData: null,
  patientId: null,
};